/* Ygeia gradient field — WebGL wash (ported from the Astro GradientField).
   Animates every .gradientfield canvas on the page; each reads its palette and
   feel from data-* attributes (see snippets/gradient-field.liquid). The first
   field initialised publishes its clock + pointer light on window.__ygField for
   elements that move in sympathy. Honours prefers-reduced-motion (one settled
   frame) and parks its loop while scrolled out of view or the tab is hidden.
   No WebGL → the canvas stays transparent and the parent's CSS fallback shows. */
(function () {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hex = (v) => {
    const h = v.trim().replace('#', '');
    return [
      parseInt(h.slice(0, 2), 16) / 255,
      parseInt(h.slice(2, 4), 16) / 255,
      parseInt(h.slice(4, 6), 16) / 255,
    ];
  };

  const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main() {
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

  const FRAG = `
precision highp float;

uniform float uTime;
uniform float uSeed;
uniform vec2 uResolution;
uniform vec3 uTopColor;
uniform vec3 uBottomColor;
uniform vec3 uAccentColor;
uniform vec3 uDarkColor;
uniform vec2 uFocusPoint;
uniform float uFocusStrength;
uniform float uGrainStatic;
uniform float uGrainAnimated;
uniform float uFadeIn;

varying vec2 vUv;

mat2 Rot(float a) {
  float s = sin(a);
  float c = cos(a);
  return mat2(c, -s, s, c);
}

vec2 hash(vec2 p) {
  p = vec2(dot(p, vec2(2127.1, 81.17)), dot(p, vec2(1269.5, 283.37)));
  return fract(sin(p) * 43758.5453);
}

float noise(in vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float n = mix(mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
                mix(dot(-1.0 + 2.0 * hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                    dot(-1.0 + 2.0 * hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
  return 0.5 + 0.5 * n;
}

// Grain hash — sin-free (Dave Hoskins' hash13). The reference's
// fract(sin(x)*43758...) grain degrades into diagonal banding where GPU
// sin() precision runs out (visibly so on Apple GPUs against a dark
// palette), so both grains hash pixel coordinates instead.
float hash13(vec3 p3) {
  p3 = fract(p3 * 0.1031);
  p3 += dot(p3, p3.zyx + 31.32);
  return fract((p3.x + p3.y) * p3.z);
}

float filmGrainNoise(in vec2 px) {
  return length(vec2(hash13(vec3(px, 17.0)), hash13(vec3(px, 31.0))));
}

float gaussian(float z, float u, float o) {
  return (1.0 / (o * sqrt(2.0 * 3.1415))) * exp(-(((z - u) * (z - u)) / (2.0 * (o * o))));
}

void main() {
  vec2 uv = vUv;
  float aspectRatio = uResolution.x / uResolution.y;

  vec2 tuv = uv - 0.5;
  // uSeed offsets the temporal phase (via t) and the spatial noise sample
  // below, so sibling fields on one page evolve independently.
  float t = (uTime + uSeed) * 0.5;

  // Rotate with noise
  float degree = noise(vec2(t * 0.05, tuv.x * tuv.y + uSeed));
  tuv.y *= 1.0 / aspectRatio;
  tuv *= Rot(radians((degree - 0.5) * 720.0 + 180.0));
  tuv.y *= aspectRatio;

  // Wave warp with sine
  float frequency = 5.0;
  float amplitude = 30.0;
  float spd = t * 2.0;
  tuv.x += sin(tuv.y * frequency + spd) / amplitude;
  tuv.y += sin(tuv.x * frequency * 1.5 + spd) / (amplitude * 0.5);

  // Two directional gradient layers, mixed vertically
  vec3 layer1 = mix(uAccentColor, uDarkColor, smoothstep(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
  vec3 layer2 = mix(uBottomColor, uTopColor, smoothstep(-0.3, 0.2, (tuv * Rot(radians(-5.0))).x));
  vec3 col = mix(layer1, layer2, smoothstep(0.5, -0.3, tuv.y));

  // Radial glow anchored to the pointer, edges riding the warp
  vec2 warp = tuv - (uv - 0.5);
  vec2 delta = (uv - 0.5) - (uFocusPoint - 0.5) + warp * 0.5;
  delta.x *= aspectRatio;
  float radialMask = smoothstep(0.6, 0.0, length(delta)) * uFocusStrength;
  col = mix(col, mix(col, uTopColor, 0.45), radialMask);

  // Fixed film grain, hashed per-pixel
  vec2 px = uv * uResolution;
  col = col - filmGrainNoise(px) * uGrainStatic;

  // Flickering grain, gaussian-weighted, strongest in the shadows —
  // re-rolled every frame (any change in z gives a fresh white-noise field)
  float gn = hash13(vec3(px, 7.0 + uTime * 60.0));
  gn = gaussian(gn, 0.0, 0.5 * 0.5);
  col += vec3(gn) * (1.0 - col) * uGrainAnimated;

  gl_FragColor = vec4(col * uFadeIn, uFadeIn);
}`;

  function initField(canvas) {
    const data = canvas.dataset;

    const top = hex(data.top || '#c9bfb3');
    const accent = hex(data.accent || '#a0a096');
    const bottom = hex(data.bottom || '#6b726a');
    const dark = hex(data.dark || '#444643');
    const speed = Number(data.speed || 1);
    const seed = Number(data.seed || 0);
    const focusStrength = Number(data.focusStrength || 0.55);
    const grainStatic = Number(data.grainStatic || 0.1);
    const grainAnimated = Number(data.grainAnimated || 0.05);

    // Pointer glow state — the loop eases current toward target at 0.04/frame.
    // y is GL-up, so it's flipped from client coords. Until a pointer shows up,
    // a faint bloom rests a little above centre.
    const target = { x: 0.5, y: 0.7, s: 0.3 };
    const current = { x: 0.5, y: 0.7, s: 0 };

    // The first field initialised is the page's "room light" — it publishes
    // clock + glow for dependents (e.g. a hero mark's sheen).
    let shared = null;
    if (!window.__ygField) {
      shared = { t: 0, x: 0.5, y: 0.7, s: 0 };
      window.__ygField = shared;
    }

    let time = 0; // accumulated seconds — pauses with the tab
    let last = 0;
    let fade = 0;
    let raf = 0;
    let live = false;
    let inView = true;
    let fit = () => {};
    let redraw = () => {};
    let gl = null; // hoisted so the viewport observer can release/restore it
    let loseExt = null; // WEBGL_lose_context handle, for off-screen release

    const restart = () => {
      if (!live || reduced) return;
      cancelAnimationFrame(raf);
      last = performance.now();
      raf = requestAnimationFrame(loop);
    };

    function loop(now) {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      time += dt;
      fade = Math.min(fade + dt / 0.15, 1);
      current.x += (target.x - current.x) * 0.04;
      current.y += (target.y - current.y) * 0.04;
      current.s += (target.s - current.s) * 0.04;
      redraw();
      if (!document.hidden && inView) raf = requestAnimationFrame(loop);
    }

    function setup() {
      gl = canvas.getContext('webgl', { antialias: true, alpha: true });
      if (!gl) return;
      loseExt = gl.getExtension('WEBGL_lose_context');

      const compile = (type, src) => {
        const sh = gl.createShader(type);
        gl.shaderSource(sh, src);
        gl.compileShader(sh);
        return sh;
      };
      const prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
      gl.useProgram(prog);

      // One triangle that overscans the viewport — cheapest full-bleed quad.
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(prog, 'aPos');
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      const U = (n) => gl.getUniformLocation(prog, n);
      gl.uniform3fv(U('uTopColor'), top);
      gl.uniform3fv(U('uAccentColor'), accent);
      gl.uniform3fv(U('uBottomColor'), bottom);
      gl.uniform3fv(U('uDarkColor'), dark);
      gl.uniform1f(U('uGrainStatic'), grainStatic);
      gl.uniform1f(U('uGrainAnimated'), grainAnimated);
      gl.uniform1f(U('uSeed'), seed);

      const uTime = U('uTime');
      const uFade = U('uFadeIn');
      const uRes = U('uResolution');
      const uFocusPoint = U('uFocusPoint');
      const uFocusStrength = U('uFocusStrength');

      redraw = () => {
        if (shared) {
          shared.t = time * speed;
          shared.x = current.x;
          shared.y = current.y;
          shared.s = current.s;
        }
        gl.uniform1f(uTime, time * speed);
        gl.uniform1f(uFade, fade);
        gl.uniform2f(uFocusPoint, current.x, current.y);
        gl.uniform1f(uFocusStrength, current.s);
        gl.drawArrays(gl.TRIANGLES, 0, 3);
      };

      fit = () => {
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const w = Math.floor(canvas.clientWidth * dpr);
        const h = Math.floor(canvas.clientHeight * dpr);
        if (canvas.width !== w || canvas.height !== h) {
          canvas.width = w;
          canvas.height = h;
        }
        gl.viewport(0, 0, w, h);
        gl.uniform2f(uRes, w, h);
        if (reduced) redraw();
      };

      fit();

      if (reduced) {
        // A settled, asymmetric moment of the field — no motion, full grain.
        time = 7.3;
        fade = 1;
        current.s = target.s;
        redraw();
        return;
      }

      live = true;
      restart();
    }

    window.addEventListener('resize', () => fit());
    if (window.visualViewport) window.visualViewport.addEventListener('resize', () => fit());

    if (!reduced) {
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) restart();
      });

      // The light is mapped into this canvas's own box, so each section's glow
      // tracks the cursor over it (and drifts off-edge when it's elsewhere).
      window.addEventListener('pointermove', (e) => {
        const r = canvas.getBoundingClientRect();
        target.x = (e.clientX - r.left) / r.width;
        target.y = 1 - (e.clientY - r.top) / r.height;
        target.s = focusStrength;
      });
    }

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      live = false;
      cancelAnimationFrame(raf);
    });
    canvas.addEventListener('webglcontextrestored', () => setup());

    // WebGL allows only ~16 live contexts per page; a homepage of washes
    // (the quad below the hero, the featured cells, the trio band) sails past
    // that and the browser silently drops the oldest — a tile goes blank. So a
    // field holds a context only while it's near the viewport: acquire on
    // approach, release once it's been scrolled well away (after a grace delay
    // so a quick scroll-by doesn't thrash). A released field falls back to the
    // parent's CSS gradient — the canvas is transparent without a context.
    // This also subsumes the old "park the loop off-screen" behaviour.
    let releaseTimer = 0;

    const acquire = () => {
      clearTimeout(releaseTimer);
      if (!gl) {
        setup(); // first acquisition — create the context and start
      } else if (gl.isContextLost()) {
        if (loseExt) loseExt.restoreContext(); // → webglcontextrestored → setup()
        else setup();
      } else {
        restart(); // context already live — just resume the loop
      }
    };

    const release = () => {
      live = false;
      cancelAnimationFrame(raf);
      if (gl && !gl.isContextLost() && loseExt) loseExt.loseContext();
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            inView = entry.isIntersecting;
            if (inView) acquire();
            else releaseTimer = setTimeout(release, 1200);
          }
        },
        { rootMargin: '300px' }
      ).observe(canvas);
    } else {
      setup();
    }
  }

  function initAll() {
    document.querySelectorAll('.gradientfield').forEach((canvas) => {
      if (canvas.dataset.gfInit) return;
      canvas.dataset.gfInit = '1';
      initField(canvas);
    });
  }

  initAll();
  // Re-init fields added in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
